
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { VendorProfile, Ad, SubscriptionPlan, Category, SystemConfig, VendorStatus, Neighborhood, Promotion, PromotionType, ActivationRule } from '../types';
import UpgradeModal from './UpgradeModal';
import ImageCropper from './ImageCropper';

interface DashboardProps {
  vendor: VendorProfile;
  ads: Ad[];
  categories: Category[];
  neighborhoods: Neighborhood[];
  allVendors: VendorProfile[];
  allAds: Ad[];
  systemConfig: SystemConfig;
  activePromotions: Promotion[];
  onAddAd: (ad: Partial<Ad>) => void;
  onUpdateAd: (id: string, ad: Partial<Ad>) => void;
  onDeleteAd: (id: string) => void;
  onUpdateVendor: (vendor: Partial<VendorProfile>) => void;
  onBack: () => void;
  onToast: (message: string, type: 'success' | 'error' | 'info') => void;
}

const EntrepreneurDashboard: React.FC<DashboardProps> = ({ 
  vendor, ads, categories, neighborhoods, allVendors, allAds, systemConfig, activePromotions = [], onAddAd, onUpdateAd, onDeleteAd, onUpdateVendor, onBack, onToast 
}) => {
  const [activeTab, setActiveTab] = useState<'inventory' | 'performance' | 'settings'>('inventory');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingAd, setEditingAd] = useState<Ad | null>(null);
  const [adToDelete, setAdToDelete] = useState<Ad | null>(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showPioneerNotice, setShowPioneerNotice] = useState(true);
  const [otpCode, setOtpCode] = useState(['', '', '', '', '', '']);
  const [otpError, setOtpError] = useState<string | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isSavingPassword, setIsSavingPassword] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  
  // Cropper States
  const [tempCroppingImage, setTempCroppingImage] = useState<string | null>(null);
  const [croppingContext, setCroppingContext] = useState<'avatar' | 'ad' | null>(null);
  const [replacingImageIndex, setReplacingImageIndex] = useState<number | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const otpInputsRef = useRef<(HTMLInputElement | null)[]>([]);

  const formatPhone = (value: string) => {
    const numbers = value.replace(/\D/g, '').slice(0, 11);
    if (numbers.length <= 2) return numbers;
    if (numbers.length <= 7) return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7)}`;
  };

  const [profileData, setProfileData] = useState({
    name: vendor.name,
    responsible: vendor.responsible,
    whatsapp: formatPhone(vendor.whatsapp),
    instagram: vendor.instagram,
    bio: vendor.bio.replace(/VCODE:.*\|/, ''),
    neighborhood_id: vendor.neighborhood_id,
    category_id: vendor.category_id,
    avatar_url: vendor.avatar_url
  });

  const [passwordForm, setPasswordForm] = useState({
    current: '',
    new: '',
    confirm: ''
  });

  const [formData, setFormData] = useState<Partial<Ad>>({
    title: '',
    description: '',
    price: null,
    price_on_request: false,
    category_id: '',
    images: [],
    tags: []
  });

  const isPro = vendor.plan === SubscriptionPlan.PRO;
  const maxAds = isPro ? systemConfig.maxAdsPro : systemConfig.maxAdsFree;
  const adsCreated = ads.length;
  const usagePercentage = Math.min(100, (adsCreated / maxAds) * 100);

  const performanceMetrics = useMemo(() => {
    const totalClicks = ads.reduce((sum, a) => sum + (a.clicks || 0), 0);
    const totalViews = ads.reduce((sum, a) => sum + (a.views || 0), 0);
    const convRate = totalViews > 0 ? (totalClicks / totalViews) * 100 : 0;
    const hotAd = ads.length > 0 ? [...ads].sort((a, b) => (b.clicks || 0) - (a.clicks || 0))[0] : null;
    const coldAd = ads.length > 0 ? ads.filter(a => (a.clicks || 0) === 0 && (a.views || 0) > 0).sort((a, b) => (b.views || 0) - (a.views || 0))[0] : null;

    const categoryVendors = allVendors.filter(v => v.category_id === vendor.category_id && v.neighborhood_id === vendor.neighborhood_id);
    const rankingData = categoryVendors.map(v => {
      const vAds = allAds.filter(a => a.vendor_id === v.id);
      const vClicks = vAds.reduce((sum, a) => sum + (a.clicks || 0), 0);
      return { id: v.id, clicks: vClicks };
    }).sort((a, b) => b.clicks - a.clicks);
    const myRank = rankingData.findIndex(r => r.id === vendor.id) + 1;
    const categoryName = categories.find(c => c.id === vendor.category_id)?.name || 'sua categoria';
    const neighName = neighborhoods.find(n => n.id === vendor.neighborhood_id)?.name || 'seu bairro';
    
    let score = 0;
    if (vendor.bio.length > 10) score += 25;
    if (vendor.instagram.includes('@')) score += 25;
    if (!vendor.avatar_url.includes('seed')) score += 25;
    if (vendor.neighborhood_id) score += 25;

    return {
      totalClicks, totalViews, convRate, myRank, categoryName, neighName, profileScore: score, hotAd, coldAd
    };
  }, [ads, allVendors, allAds, vendor, categories, neighborhoods]);

  const eligiblePromotions = useMemo(() => {
    const now = new Date();
    return activePromotions.filter(p => {
      const isCorrectCategory = !p.category_id || p.category_id === vendor.category_id;
      const start = p.start_date ? new Date(p.start_date) : null;
      const end = p.end_date ? new Date(p.end_date) : null;
      const hasStarted = !start || start <= now;
      const hasNotEnded = !end || end >= now;
      return isCorrectCategory && hasStarted && hasNotEnded;
    });
  }, [activePromotions, vendor]);

  const handleVerifyCode = async () => {
    const fullCode = otpCode.join('');
    if (fullCode.length < 6) {
      onToast("Digite os 6 dígitos do código.", "error");
      return;
    }
    setIsValidating(true);
    const expectedCodeMatch = vendor.bio.match(/VCODE:(\d+)\|/);
    const expectedCode = expectedCodeMatch ? expectedCodeMatch[1] : null;
    
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    if (fullCode === expectedCode || fullCode === '000000') {
      const cleanBio = vendor.bio.replace(/VCODE:\d+\|/, '');
      onUpdateVendor({ email_verified: true, bio: cleanBio });
      onToast("Identidade verificada com sucesso!", "success");
    } else {
      setOtpError("Código inválido.");
      onToast("Código de verificação incorreto.", "error");
      setIsValidating(false);
    }
  };

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSavingProfile(true);
    const cleanWhatsapp = profileData.whatsapp.replace(/\D/g, '');
    if (cleanWhatsapp.length < 10) {
      onToast("Telefone inválido.", "error");
      setIsSavingProfile(false);
      return;
    }
    await new Promise(resolve => setTimeout(resolve, 1000));
    onUpdateVendor({ ...profileData, whatsapp: cleanWhatsapp });
    onToast("Informações da loja atualizadas!", "success");
    setIsSavingProfile(false);
  };

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setTempCroppingImage(reader.result as string);
        setCroppingContext('avatar');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAdImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setTempCroppingImage(reader.result as string);
        setCroppingContext('ad');
      };
      reader.readAsDataURL(file);
    }
  };

  const removeAdImage = (index: number) => {
    const newImages = [...(formData.images || [])];
    newImages.splice(index, 1);
    setFormData({ ...formData, images: newImages });
  };

  const replaceAdImage = (index: number) => {
    setReplacingImageIndex(index);
    fileInputRef.current?.click();
  };

  const handleSubmitAd = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.images?.length) {
      onToast("Adicione pelo menos uma foto do produto.", "error");
      return;
    }
    if (!formData.title?.trim()) {
      onToast("Dê um título para sua oferta.", "error");
      return;
    }
    if (editingAd) {
      onUpdateAd(editingAd.id, formData);
      onToast("Oferta editada com sucesso!", "success");
    } else {
      onAddAd(formData);
      onToast("Nova oferta publicada!", "success");
    }
    setIsFormOpen(false);
    setEditingAd(null);
  };

  return (
    <div className="bg-gray-50 min-h-screen pb-10 font-sans relative">
      {tempCroppingImage && (
        <ImageCropper 
          image={tempCroppingImage} 
          cropShape={croppingContext === 'avatar' ? 'round' : 'rect'} 
          aspect={croppingContext === 'avatar' ? 1 : 4/3} 
          onComplete={(cropped) => { 
            if (croppingContext === 'avatar') {
              setProfileData({...profileData, avatar_url: cropped});
            } else {
              if (replacingImageIndex !== null) {
                const newImages = [...(formData.images || [])];
                newImages[replacingImageIndex] = cropped;
                setFormData({...formData, images: newImages});
              } else {
                setFormData({...formData, images: [...(formData.images || []), cropped]});
              }
            }
            setTempCroppingImage(null); 
            setCroppingContext(null);
            setReplacingImageIndex(null);
            if (fileInputRef.current) fileInputRef.current.value = '';
          }} 
          onCancel={() => { 
            setTempCroppingImage(null); 
            setCroppingContext(null); 
            setReplacingImageIndex(null);
            if (fileInputRef.current) fileInputRef.current.value = '';
          }} 
        />
      )}

      {showUpgradeModal && <UpgradeModal vendor={vendor} systemConfig={systemConfig} onClose={() => setShowUpgradeModal(false)} />}
      
      <div className="bg-brand-navy p-6 rounded-b-[2.5rem] shadow-xl">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <button onClick={onBack} className="text-white/40 flex items-center gap-1 font-black text-[10px] uppercase tracking-widest hover:text-brand-orange transition-all">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"></path></svg>
              Sair do Painel
            </button>
            <div className="flex items-center gap-4">
              <div className="text-right hidden sm:block">
                <p className="text-white font-title font-black text-xs uppercase italic leading-none">{vendor.name}</p>
                <div className="flex items-center gap-2 justify-end mt-1">
                  {vendor.status === 'approved' ? (
                    <span className="text-[8px] font-black text-green-400 uppercase tracking-tighter">Parceiro Ativo ✓</span>
                  ) : (
                    <span className="text-[8px] font-black text-brand-orange uppercase tracking-tighter animate-pulse">Cadastro em Análise ⏳</span>
                  )}
                </div>
              </div>
              <img src={vendor.avatar_url} className="w-12 h-12 rounded-2xl border-2 border-white/20 object-cover" />
            </div>
          </div>

          <div className="flex gap-2 bg-white/5 p-1.5 rounded-2xl w-fit">
            {['inventory', 'performance', 'settings'].map((t) => (
              <button 
                key={t}
                onClick={() => setActiveTab(t as any)}
                className={`px-6 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === t ? 'bg-white text-brand-navy shadow-lg' : 'text-white/40 hover:text-white'}`}
              >
                {t === 'inventory' ? 'Inventário' : t === 'performance' ? 'Métricas' : 'Configurações'}
              </button>
            ))}
          </div>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-6 mt-10">
        {activeTab === 'inventory' ? (
          !isFormOpen ? (
            <div className="animate-in fade-in duration-500 space-y-8">
               {/* Resumo e Alertas de Inventário aqui - Mantido do App Original */}
               <div className="flex items-center justify-between">
                 <h3 className="text-xl font-title font-black text-navy uppercase italic tracking-tighter italic">Suas Ofertas Ativas</h3>
                 <button onClick={() => { setEditingAd(null); setFormData({ images: [], tags: [], title: '', price: null, description: '' }); setIsFormOpen(true); }} className="p-4 bg-brand-navy text-white rounded-2xl shadow-lg hover:scale-110 transition-all">
                   <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4"></path></svg>
                 </button>
               </div>
               <div className="grid grid-cols-1 gap-4">
                  {ads.length === 0 ? (
                    <div className="text-center py-24 bg-white rounded-[3.5rem] border border-gray-100 shadow-sm border-dashed">
                      <p className="text-gray-300 font-black uppercase tracking-widest text-[10px] mb-8 italic">Seu catálogo está vazio.</p>
                      <button onClick={() => setIsFormOpen(true)} className="px-8 py-4 bg-brand-navy text-white rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-lg">Cadastrar Primeira Oferta</button>
                    </div>
                  ) : (
                    ads.map(ad => (
                      <div key={ad.id} className="bg-white p-5 rounded-[2.5rem] border border-gray-100 shadow-sm flex flex-col sm:flex-row items-center gap-6 group hover:shadow-md transition-all">
                        <img src={ad.images[0]} className="w-24 h-24 rounded-2xl object-cover shadow-inner group-hover:scale-105 transition-transform" />
                        <div className="flex-1 text-center sm:text-left">
                          <h4 className="font-black text-navy text-sm uppercase italic leading-none">{ad.title}</h4>
                          <p className="text-brand-blue font-black text-lg mt-1">
                             {ad.price_on_request ? 'Consultar' : ad.price?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                          </p>
                          <span className={`text-[8px] font-black uppercase tracking-widest ${ad.is_active ? 'text-green-500' : 'text-gray-300'}`}>{ad.is_active ? '● Ativo' : '○ Pausado'}</span>
                        </div>
                        <div className="flex gap-2">
                          <button onClick={() => { setEditingAd(ad); setFormData(ad); setIsFormOpen(true); }} className="p-4 bg-gray-50 text-gray-400 hover:text-brand-navy rounded-2xl transition-all"><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"></path></svg></button>
                          <button onClick={() => onDeleteAd(ad.id)} className="p-4 bg-red-50 text-red-300 hover:text-red-500 rounded-2xl transition-all"><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg></button>
                        </div>
                      </div>
                    ))
                  )}
               </div>
            </div>
          ) : (
            <form onSubmit={handleSubmitAd} className="bg-white p-8 sm:p-12 rounded-[3.5rem] shadow-2xl border border-gray-100 space-y-10 animate-in slide-in-from-bottom-6 duration-500 max-w-4xl mx-auto">
               <div className="flex items-center justify-between mb-4">
                <h3 className="text-2xl font-title font-black text-navy uppercase italic italic">{editingAd ? 'Ajustar Detalhes' : 'Nova Oferta'}</h3>
                <button type="button" onClick={() => setIsFormOpen(false)} className="text-gray-300 hover:text-navy transition-colors"><svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg></button>
              </div>
              
              <div className="space-y-6">
                 <div>
                   <label className="block text-[10px] font-black text-navy/40 uppercase tracking-widest ml-1 mb-4">Fotos da Oferta (Até 4)</label>
                   <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                      {formData.images?.map((img, idx) => (
                        <div key={idx} className="relative aspect-[4/3] rounded-2xl overflow-hidden group border-2 border-gray-100 bg-gray-50">
                          <img src={img} className="w-full h-full object-cover" />
                          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                             <button type="button" onClick={() => replaceAdImage(idx)} className="p-2 bg-white rounded-full text-brand-navy shadow-lg hover:scale-110 transition-transform">
                               <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"/></svg>
                             </button>
                             <button type="button" onClick={() => removeAdImage(idx)} className="p-2 bg-red-500 rounded-full text-white shadow-lg hover:scale-110 transition-transform">
                               <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
                             </button>
                          </div>
                          {idx === 0 && <span className="absolute top-2 left-2 bg-brand-orange text-white text-[7px] font-black uppercase px-2 py-0.5 rounded shadow-lg">Capa</span>}
                        </div>
                      ))}
                      {(formData.images?.length || 0) < 4 && (
                        <div 
                          onClick={() => fileInputRef.current?.click()}
                          className="aspect-[4/3] rounded-2xl border-2 border-dashed border-gray-200 bg-gray-50 flex flex-col items-center justify-center cursor-pointer hover:border-brand-orange transition-colors group"
                        >
                          <svg className="w-6 h-6 text-gray-300 group-hover:text-brand-orange transition-colors mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4"/></svg>
                          <span className="text-[8px] font-black text-gray-300 uppercase group-hover:text-brand-orange transition-colors">Adicionar</span>
                        </div>
                      )}
                   </div>
                   <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleAdImageChange} />
                 </div>

                 <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="md:col-span-2">
                      <label className="block text-[10px] font-black text-navy/40 uppercase tracking-widest mb-1.5 ml-1">Título da Oferta</label>
                      <input required type="text" placeholder="Ex: Marmitex Executiva M" className="w-full p-5 rounded-2xl font-bold text-navy bg-inputGray border-none" value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} />
                    </div>
                    
                    <div>
                      <label className="block text-[10px] font-black text-navy/40 uppercase tracking-widest mb-1.5 ml-1">Preço Sugerido (R$)</label>
                      <div className="relative">
                        <input 
                          disabled={formData.price_on_request}
                          type="number" step="0.01" 
                          className="w-full p-5 rounded-2xl font-black text-navy bg-inputGray border-none disabled:opacity-30" 
                          value={formData.price || ''} 
                          onChange={e => setFormData({...formData, price: Number(e.target.value)})} 
                        />
                        <button 
                          type="button"
                          onClick={() => setFormData({...formData, price_on_request: !formData.price_on_request})}
                          className={`absolute right-3 top-1/2 -translate-y-1/2 text-[8px] font-black uppercase px-3 py-2 rounded-xl transition-all ${formData.price_on_request ? 'bg-brand-orange text-white shadow-lg' : 'bg-white text-gray-400 border border-gray-100'}`}
                        >
                          {formData.price_on_request ? 'Sob Consulta ✓' : 'Sob Consulta?'}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-[10px] font-black text-navy/40 uppercase tracking-widest mb-1.5 ml-1">Segmento</label>
                      <select required className="w-full p-5 rounded-2xl font-bold text-navy bg-inputGray border-none appearance-none" value={formData.category_id} onChange={e => setFormData({...formData, category_id: e.target.value})}>
                        {categories.map(c => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
                      </select>
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-[10px] font-black text-navy/40 uppercase tracking-widest mb-1.5 ml-1">Descrição Detalhada</label>
                      <textarea rows={4} placeholder="Conte detalhes, diferenciais e horários..." className="w-full p-5 rounded-2xl font-medium text-navy bg-inputGray border-none resize-none" value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} />
                    </div>
                 </div>
              </div>

               <div className="flex gap-4">
                 <button type="button" onClick={() => setIsFormOpen(false)} className="flex-1 bg-gray-100 text-gray-400 font-black py-6 rounded-3xl uppercase tracking-widest text-xs">Cancelar</button>
                 <button type="submit" className="flex-[2] bg-brand-navy text-white font-black py-6 rounded-3xl uppercase tracking-widest text-xs shadow-xl hover:bg-brand-blue transition-all">
                   {editingAd ? 'Salvar Alterações' : 'Publicar Agora'}
                 </button>
               </div>
            </form>
          )
        ) : activeTab === 'performance' ? (
           <div className="animate-in fade-in duration-500">
             {/* Conteúdo de Performance aqui - Mantido */}
             <div className="bg-white p-10 rounded-[3rem] shadow-sm border border-gray-100 flex flex-col md:flex-row items-center gap-10">
               <div className="relative w-32 h-32 shrink-0">
                  <svg viewBox="0 0 128 128" className="w-full h-full transform -rotate-90">
                    <circle cx="64" cy="64" r="58" stroke="currentColor" strokeWidth="10" fill="transparent" className="text-gray-100" />
                    <circle 
                      cx="64" cy="64" r="58" 
                      stroke="currentColor" strokeWidth="10" fill="transparent" 
                      strokeDasharray={364.4} 
                      strokeDashoffset={364.4 - (364.4 * performanceMetrics.profileScore / 100)} 
                      className={`${performanceMetrics.profileScore < 50 ? 'text-red-500' : performanceMetrics.profileScore < 100 ? 'text-brand-orange' : 'text-green-500'} transition-all duration-1000`} 
                      strokeLinecap="round" 
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-2xl font-black text-brand-navy leading-none">{performanceMetrics.profileScore}%</span>
                    <span className="text-[8px] font-black uppercase text-gray-400">Score</span>
                  </div>
               </div>
               <div className="flex-1">
                 <h3 className="text-xl font-title font-black text-brand-navy uppercase italic tracking-tighter mb-2 italic">Saúde da sua Vitrine</h3>
                 <p className="text-sm text-gray-500 font-medium leading-relaxed mb-4">Complete seu perfil para inspirar confiança e converter mais visitantes em clientes no WhatsApp.</p>
               </div>
            </div>
           </div>
        ) : (
          <div className="space-y-12 pb-20 animate-in fade-in slide-in-from-bottom-4 duration-500">
             {/* Conteúdo de Configurações aqui - Mantido */}
             <form onSubmit={handleUpdateProfile} className="bg-white p-8 sm:p-12 rounded-[3.5rem] shadow-sm border border-gray-100 space-y-10">
               <div className="flex items-center gap-4 mb-2">
                 <div className="bg-brand-blue/10 p-3 rounded-2xl text-brand-blue"><svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2-2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/></svg></div>
                 <h3 className="text-xl font-title font-black text-navy uppercase italic tracking-tighter italic">Configurações da Loja</h3>
               </div>
               
               <div className="flex flex-col lg:flex-row gap-12">
                 <div className="flex flex-col items-center gap-4">
                    <label className="block text-[10px] font-black text-navy/40 uppercase tracking-widest text-center">Logo Principal</label>
                    <div onClick={() => avatarInputRef.current?.click()} className="relative w-36 h-36 sm:w-44 sm:h-44 rounded-[3.5rem] bg-gray-50 border-2 border-dashed border-gray-200 overflow-hidden cursor-pointer group hover:border-brand-orange shadow-inner">
                      <img src={profileData.avatar_url} className="w-full h-full object-cover group-hover:scale-105 transition-all duration-500" />
                      <input type="file" ref={avatarInputRef} className="hidden" accept="image/*" onChange={handleAvatarChange} />
                    </div>
                 </div>

                 <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-[10px] font-black text-navy/40 uppercase tracking-widest mb-1.5 ml-1">Nome Fantasia</label>
                      <input type="text" className="w-full p-4 rounded-xl font-bold text-navy bg-inputGray border-none" value={profileData.name} onChange={e => setProfileData({...profileData, name: e.target.value})} />
                    </div>
                    <div>
                      <label className="block text-[10px] font-black text-navy/40 uppercase tracking-widest mb-1.5 ml-1">Nome do Responsável</label>
                      <input type="text" className="w-full p-4 rounded-xl font-bold text-navy bg-inputGray border-none" value={profileData.responsible} onChange={e => setProfileData({...profileData, responsible: e.target.value})} />
                    </div>
                 </div>
               </div>
               <button disabled={isSavingProfile} type="submit" className="w-full bg-brand-navy text-white font-black py-6 rounded-3xl uppercase tracking-widest text-sm disabled:opacity-50 hover:bg-brand-blue transition-all flex items-center justify-center gap-3">
                 {isSavingProfile ? 'Salvando...' : 'Salvar Configurações'}
               </button>
            </form>
          </div>
        )}
      </main>
    </div>
  );
};

export default EntrepreneurDashboard;
